import { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import {
  terminalManagementApi,
  type TerminalDataSourceDto,
  type TerminalFilterInput,
  type TerminalRequestHistoryDto,
} from '@/api/services/terminalManagement';
import { TerminalRequestModal } from './TerminalRequestModal';

function pad(n: number) {
  return String(n).padStart(2, '0');
}
const fmtTs = (iso: string | null) => {
  if (!iso) return 'N/A';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const textOrNa = (v: string | null) => (v == null || v === '' ? 'N/A' : v);

function statusBadge(s: string) {
  const v = s.toLowerCase();
  const cls = v === 'approved' ? 'text-bg-success' : v === 'rejected' ? 'text-bg-danger' : 'text-bg-warning';
  return <span className={`badge ${cls}`}>{s}</span>;
}

function LpsTab() {
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; row?: TerminalDataSourceDto } | null>(null);
  const table = useServerDataTable<TerminalDataSourceDto, TerminalFilterInput>({
    queryKey: ['terminal-management', 'terminals'],
    fetcher: (req) => terminalManagementApi.getTerminals(req),
    pageSize: 5,
  });

  return (
    <>
      <DataGrid
        columns={[
          { key: 'name', title: 'Name', className: 'text-start', value: (r) => r.name },
          { key: 'server', title: 'Server', className: 'text-start', filterable: false, value: (r) => r.server, cell: (r) => textOrNa(r.server) },
          { key: 'accountLogin', title: 'Account Login', className: 'text-start', filterable: false, value: (r) => r.accountLogin, cell: (r) => textOrNa(r.accountLogin) },
          { key: 'password', title: 'Password', className: 'text-start', filterable: false, sortable: false, value: (r) => r.password, cell: (r) => (r.password ? '••••••••' : 'N/A') },
          { key: 'enabled', title: 'Status', className: 'text-start', filterable: false, value: (r) => (r.enabled ? 'Enabled' : 'Disabled'), cell: (r) => <span className={`badge ${r.enabled ? 'text-bg-success' : 'text-bg-danger'}`}>{r.enabled ? 'Enabled' : 'Disabled'}</span> },
          { key: 'connectionType', title: 'Connection Type', className: 'text-start', value: (r) => r.connectionType },
          { key: 'hasPendingRequest', title: 'Pending', className: 'text-start', filterable: false, sortable: false, value: (r) => (r.hasPendingRequest ? 'Pending' : 'None'), cell: (r) => (r.hasPendingRequest ? <span className="badge text-bg-warning" title={r.pendingName ?? undefined}>Pending</span> : <span className="text-muted">None</span>) },
        ] as DataGridColumn<TerminalDataSourceDto>[]}
        data={table.rows}
        rowKey={(r) => String(r.id)}
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={(key, value) => table.setColumnFilter(key, value)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        toolbarStart={
          <>
            <button type="button" className="btn btn-sm btn-primary" title="Add New LP" onClick={() => setModal({ mode: 'add' })}>
              <i className="fa fa-plus me-1" />Add New LP
            </button>
            <button type="button" className="btn btn-sm btn-outline-primary" title="Reload" onClick={() => table.refetch()}>
              <i className="fa fa-refresh" />
            </button>
          </>
        }
        rowActions={(row) => (
          <button type="button" className="btn btn-sm btn-outline-primary" title="Request update" onClick={() => setModal({ mode: 'edit', row })}>
            <i className="fa fa-pencil" />
          </button>
        )}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />
      {modal && (
        <TerminalRequestModal
          mode={modal.mode}
          row={modal.row}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); void table.refetch(); }}
        />
      )}
    </>
  );
}

function HistoryTab() {
  const table = useServerDataTable<TerminalRequestHistoryDto>({
    queryKey: ['terminal-management', 'history'],
    fetcher: (req) => terminalManagementApi.getRequestHistory(req),
    pageSize: 5,
  });

  return (
    <DataGrid
      columns={[
        { key: 'terminalName', title: 'Terminal', className: 'text-start', value: (r) => r.terminalName },
        { key: 'requestType', title: 'Type', className: 'text-start', value: (r) => r.requestType },
        { key: 'status', title: 'Status', className: 'text-start', value: (r) => r.status, cell: (r) => statusBadge(r.status) },
        { key: 'requestedBy', title: 'Requested By', className: 'text-start', value: (r) => r.requestedBy },
        { key: 'requestDate', title: 'Requested', className: 'text-start', value: (r) => r.requestDate, cell: (r) => fmtTs(r.requestDate) },
        { key: 'approvalDate', title: 'Approved', className: 'text-start', value: (r) => r.approvalDate, cell: (r) => fmtTs(r.approvalDate) },
      ] as DataGridColumn<TerminalRequestHistoryDto>[]}
      data={table.rows}
      rowKey={(r) => r.id}
      emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
      toolbarStart={
        <button type="button" className="btn btn-sm btn-outline-primary" title="Reload" onClick={() => table.refetch()}>
          <i className="fa fa-refresh" />
        </button>
      }
      server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
    />
  );
}

const TABS: TabItem[] = [
  { key: 'lps', title: 'LPs' },
  { key: 'history', title: 'History' },
];

export function TerminalManagementPage() {
  const [active, setActive] = useState('lps');
  return (
    <div className="terminal-management-page">
      <PageHeader title="LP Management" breadcrumb={['Home', 'LP Management']} />
      <div className="card custom-card mb-2">
        <div className="card-header p-0"><Tabs tabs={TABS} active={active} onChange={setActive} variant="underline" /></div>
        <div className="card-body">
          {active === 'lps' && <LpsTab />}
          {active === 'history' && <HistoryTab />}
        </div>
      </div>
    </div>
  );
}
